import React from "react";
import { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  elements,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useRef } from "react";
import { saveAs } from "file-saver";
import { Button } from "@mui/material";
import { Chart, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { graph_xbar } from "./GraphXBar";
import SimpleBackdrop from "./Backdrop";
import { APIServices } from "../helpers/APIServices";
import { exportDataALL } from "../helpers/common";
import "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import JSZip from "jszip";
import { injectImageIfPlaceholderExists, getImageUrl } from "../helpers/common";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

export default function LineChartForNormalQT({
  arrData,
  chartName,
  dataSetName,
  id,
  ucl,
  cl,
  lcl,
  idQuestion,
  partName,
  partNo,
  char,
  min,
  max,
  spec,
  torelance1,
  torelance2,
  image,
  dataAll,
  selectedRows = [],
  customer = "",
}) {
  let createdAtArray = [];
  console.log(dataAll);

  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);

  const downloadGraph = async () => {
    if (selectedRows.length === 0) {
      alert("กรุณาเปิด Checkbox เลือกข้อมูลอย่างน้อย 1 รายการก่อนทำการ Export");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        "/templates/Xbar R Chart_app.xlsx?v=" + new Date().getTime(),
      );
      let arrayBuffer = await response.arrayBuffer();

      if (image) {
        try {
          const fullImageUrl = getImageUrl(image);
          //console.log("[Export LineChart Debug] image full URL:", fullImageUrl);
          const imgRes = await fetch(fullImageUrl);
          const imgBlob = await imgRes.blob();
          const imgArrayBuffer = await imgBlob.arrayBuffer();
          arrayBuffer = await injectImageIfPlaceholderExists(
            arrayBuffer,
            imgArrayBuffer,
          );
        } catch (imgError) {
          console.error("Could not override image for excel", imgError);
        }
      }

      const workbook = await window.XlsxPopulate.fromDataAsync(arrayBuffer);
      const sheet = workbook.sheet(0);

      if (partNo) sheet.cell("C2").value(partNo);
      if (partName) sheet.cell("O2").value(partName);
      if (max !== undefined) sheet.cell("D14").value(Number(max));
      if (min !== undefined) sheet.cell("F14").value(Number(min));

      // Spec range text in merged A+B cells at row 19 and 31
      if (min !== undefined && max !== undefined) {
        const specText = `${Number(min).toFixed(2)} - ${Number(max).toFixed(2)}`;
        sheet.cell("A19").value(specText);
        sheet.cell("A31").value(specText);
      }

      // Customer → X2, Month → X3
      if (customer) sheet.cell("X2").value(customer);

      const selectedData = dataAll.filter((item) =>
        selectedRows.includes(item.id),
      );

      if (selectedData.length > 0 && selectedData[0].date) {
        sheet
          .cell("X3")
          .value(dayjs(selectedData[0].date, "DD/MM/YYYY").format("MMMM YYYY"));
      }

      for (let row = 40; row <= 42; row++) {
        for (let col = 6; col <= 35; col++) {
          sheet.cell(row, col).value(null);
        }
      }

      selectedData.forEach((item, idx) => {
        const colNumber = 6 + idx;
        const actuals =
          item.data && item.data.actual
            ? item.data.actual
            : item.sc && item.sc.actual
              ? item.sc.actual
              : [];
        if (actuals[0] !== undefined)
          sheet.cell(40, colNumber).value(Number(actuals[0]));
        if (actuals[1] !== undefined)
          sheet.cell(41, colNumber).value(Number(actuals[1]));
        if (actuals[2] !== undefined)
          sheet.cell(42, colNumber).value(Number(actuals[2]));

        // Row 51: Job No, Row 52: Date, Row 53: Time
        if (item.jobNo) sheet.cell(51, colNumber).value(item.jobNo);
        if (item.date) sheet.cell(52, colNumber).value(item.date);
        if (item.time) sheet.cell(53, colNumber).value(item.time);
      });

      // Machine No → O3 (use first selected item's machine)
      if (selectedData.length > 0 && selectedData[0].machineNo) {
        sheet.cell("O3").value(selectedData[0].machineNo);
      }

      // Add Min/Max formulas for columns D to R (4 to 18) as requested
      for (let col = 4; col <= 18; col++) {
        const colLetter = String.fromCharCode(64 + col);
        sheet.cell(55, col).formula(`MIN(${colLetter}24:${colLetter}54)`);
        sheet.cell(56, col).formula(`MAX(${colLetter}24:${colLetter}54)`);
      }

      const blob = await workbook.outputAsync();
      saveAs(blob, `Report_Xbar_${partNo || "Export"}.xlsx`);
    } catch (e) {
      console.error("Error in Download NormalQT Template:", e);
    }
    setLoading(false);
  };

  const handleDownloadChart = () => {
    return new Promise((resolve, reject) => {
      const canvas = chartRef.current?.canvas;
      if (canvas) {
        const base64Image = canvas.toDataURL("image/png");
        resolve(base64Image);
      } else {
        reject("Canvas is not available");
      }
    });
  };

  const data = {
    labels: dataAll.map((item, index) => {
      const formattedDate = dayjs(item.createAt).format("DD-MM-YYYY");
      return `${formattedDate}`;
    }),
    datasets: [
      {
        label: dataSetName || "dataset 1",
        data: arrData,
        borderColor: "#000000",
        backgroundColor: "#000000",
        pointBorderColor: arrData.map((value) =>
          value > ucl || value < lcl ? "red" : "#000000",
        ),
        pointBackgroundColor: arrData.map((value) =>
          value > ucl || value < lcl ? "red" : "#000000",
        ),
        pointBorderWidth: arrData.map(
          (value) => (value > ucl || value < lcl ? 2 : 2), // Custom border width for red points
        ),
        pointRadius: arrData.map(
          (value) => (value > ucl || value < lcl ? 5 : 3), // Custom radius for red points
        ),
        pointHoverRadius: 6,
        pointStyle: arrData.map(
          (value) => (value > ucl || value < lcl ? "circle" : "circle"), // Custom shape for the red points
        ),
        pointHoverBorderWidth: arrData.map((value) =>
          value > ucl || value < lcl ? 2 : 1,
        ),
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: chartName ? chartName : "Chart.js Line Chart",
      },
      annotation: {
        annotations: {
          averageLine: {
            type: "line",
            yMin: cl,
            yMax: cl,
            borderColor: "rgb(0, 255, 0)",
            borderWidth: 2,
          },
          lowerControlLimit: {
            type: "line",
            yMin: lcl,
            yMax: lcl,
            borderColor: "rgb(255, 0, 0)",
            borderWidth: 2,
            borderDash: [6, 6],
          },
          upperControlLimit: {
            type: "line",
            yMin: ucl,
            yMax: ucl,
            borderColor: "rgb(255, 0, 0)",
            borderWidth: 2,
            borderDash: [6, 6],
          },
          ...arrData.reduce((annotations, value, index) => {
            if (value > ucl || value < lcl) {
              annotations[`highlightPoint${index}`] = {
                type: "label",
                xValue: index,
                yValue: value,
                backgroundColor: "rgba(245,245,245,0.8)",
                content: [`${value.toFixed(2)}`],
                borderColor: "transparent",
                borderWidth: 0,
                radius: 10,
                padding: 2,
                color: "red",

                font: {
                  size: 12,
                  weight: "bold",
                },
                textAlign: "center",
                xAdjust: 10,
                yAdjust: -15,
                clip: false, // Ensures label won't be clipped
              };
            }
            return annotations;
          }, {}),
        },
      },
    },

    scales: {
      y: {
        beginAtZero: false,
        min: lcl - 0.25,
        max: ucl + 0.25,
      },
      x: {
        beginAtZero: false,
      },
    },
    elements: {
      point: {
        clip: false,
      },
    },
  };

  return (
    <div className="xl:h-60 xl:w-full">
      <SimpleBackdrop open={loading} setOpen={setLoading} />
      <div>
        <Button
          className="bg-orangeCustom"
          variant="contained"
          onClick={downloadGraph}
          sx={{ margin: 4 }}
        >
          Download
        </Button>
      </div>
      <Line options={options} data={data} export ref={chartRef} />
    </div>
  );
}
